import { type BibleChooserData } from '../dm/Bible.js';

/**
 * Converts a serialized string of Bible versions into `BibleChooserData` objects.
 *
 * @param inputString Serialized string of Bible version(s)
 * @returns Array of `BibleChooserData` objects; could be empty if `inputString` isn't a valid string
 */
export const getBibleChooserDataFromString = (inputString: string): BibleChooserData[] => {
  const versionInstances = inputString.split('|');

  const response: BibleChooserData[] = versionInstances
    .filter((i) => {
      const split = i.split(';');
      if (split.length === 3) return true;

      if (split.length === 1 && split[0].trim().length > 0) return true;

      return false;
    })
    .map((instance) => {
      const splitString = instance.split(';');
      return {
        abbreviation: splitString[0],
        abbreviationLocal: splitString[1] || '',
        bibleId: splitString[2] || '',
      };
    });

  return response;
};

/**
 * Converts an array of `BibleChooserData` objects into a seralized string.
 *
 * Although the logic should stay internal, it is:
 *
 * * Each Bible version is separated by a `|`
 * * Data fields within a version are separated by `;`
 *
 * @param input Array of objects
 * @returns Serialized string for use in APIs, settings, etc.
 */
export const getStringFromBibleChooserData = (input?: BibleChooserData[]): string => {
  if (!input || input.length < 1) return '';

  return input.map((item) => `${item.abbreviation};${item.abbreviationLocal};${item.bibleId}`).join('|');
};

interface ValidChooserDataInputs {
  inputString?: string;
  inputArray?: BibleChooserData[];
}
/**
 * Helper function to determine if all given inputs are valid. Can
 * take either the serialized string version or data in object form.
 * (Returns false if neither is provided.)
 *
 * @param inputString Serialized `BibleChooserData` data
 * @param inpubObj Array of `BibleChooserData` objects
 * @returns True if all data is valid
 */
export const isValidBibleChooserData = ({ inputArray, inputString }: ValidChooserDataInputs = {}): boolean => {
  if (!inputArray && !inputString) return false;
  if (!inputArray && inputString === '') return false;

  const testObj: BibleChooserData[] = inputArray ? inputArray : getBibleChooserDataFromString(inputString!);

  if (testObj.length < 1) return false;

  return testObj.every((obj) => obj.abbreviation !== '' && obj.abbreviationLocal !== '' && obj.bibleId !== '');
};
